import { MFE, MFE_CONFIG } from "@sunrun/sales-experience-shared";
import { getEnv } from "./env";

const MFE_OVERRIDE_STORAGE_KEY = "sales-experience:mfe-overrides";

type MfeOverrides = Partial<Record<MFE, string>>;

const MFE_KEYS = Object.keys(MFE_CONFIG) as MFE[];

const defaultStagingOrigins: Record<MFE, string | undefined> =
  Object.fromEntries(
    MFE_KEYS.map((mfe) => [mfe, MFE_CONFIG[mfe].origin.staging])
  ) as Record<MFE, string | undefined>;

const getDefaultStagingOrigin = (mfe: MFE): string =>
  defaultStagingOrigins[mfe] ?? "";

const restoreDefaultStagingOrigins = (): void => {
  for (const mfe of MFE_KEYS) {
    const defaultOrigin = defaultStagingOrigins[mfe];
    if (defaultOrigin === undefined) {
      delete MFE_CONFIG[mfe].origin.staging;
    } else {
      MFE_CONFIG[mfe].origin.staging = defaultOrigin;
    }
  }
};

const readMfeOverrides = (): MfeOverrides => {
  try {
    const raw = sessionStorage.getItem(MFE_OVERRIDE_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const result: MfeOverrides = {};
    for (const mfe of MFE_KEYS) {
      const value = (parsed as Record<string, unknown>)[mfe];
      if (typeof value === "string" && value.trim()) {
        result[mfe] = value.trim();
      }
    }
    return result;
  } catch {
    return {};
  }
};

const writeMfeOverrides = (overrides: MfeOverrides): void => {
  const cleaned: MfeOverrides = {};
  for (const mfe of MFE_KEYS) {
    const value = overrides[mfe]?.trim();
    if (value) {
      cleaned[mfe] = value;
    }
  }
  if (Object.keys(cleaned).length === 0) {
    sessionStorage.removeItem(MFE_OVERRIDE_STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(MFE_OVERRIDE_STORAGE_KEY, JSON.stringify(cleaned));
};

const reloadWindow = (): void => {
  window.location.reload();
};

const applyMfeOverrides = (search = window.location.search): void => {
  restoreDefaultStagingOrigins();

  if (getEnv() === "staging") {
    const stored = readMfeOverrides();
    for (const mfe of MFE_KEYS) {
      const storedUrl = stored[mfe];
      if (storedUrl) {
        MFE_CONFIG[mfe].origin.staging = storedUrl;
      }
    }
  }

  const params = new URLSearchParams(search);
  for (const mfe of MFE_KEYS) {
    const injectedUrl = params.get(mfe);
    if (injectedUrl) {
      MFE_CONFIG[mfe].origin.staging = injectedUrl;
    }
  }
};

export type { MfeOverrides };
export {
  MFE_KEYS,
  MFE_OVERRIDE_STORAGE_KEY,
  applyMfeOverrides,
  getDefaultStagingOrigin,
  readMfeOverrides,
  reloadWindow,
  writeMfeOverrides,
};
